import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getProjectConfig } from '../config/projects';
import { parseDynamicVariables } from '../utils/dataParser';
import PreAssistantForm from './PreAssistantForm';
import {
  requestMicrophoneAccess,
  startWakeLock,
  unlockAudio,
} from '../utils/mobileSession';

const DEFAULT_BRANDING = {
  requireMicGate: true,
  partnerLogo: null,
  primaryColor: '#E85D04',
  backgroundColor: '#F7F5F2',
  buttonTextColor: '#FFFFFF',
  landingTitle: 'Ready to start?',
  landingSubtitle:
    'Tap below to allow microphone access and begin your voice survey. Keep this screen open while you talk.',
  landingButton: 'Allow microphone access and start',
  landingFooter:
    'Works best in Chrome or Safari. Do not lock your phone during the survey.',
};

function resolveLandingText(value, fallback) {
  return value != null ? value : fallback;
}

async function resolveProject(projectId) {
  const staticConfig = getProjectConfig(projectId);
  if (staticConfig) {
    return {
      name: staticConfig.name,
      agentId: staticConfig.agentId,
      formConfig: staticConfig.formConfig,
      dynamicVariables: staticConfig.dynamicVariables,
      ...DEFAULT_BRANDING,
    };
  }

  try {
    const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.ok || !data.project) return null;
    const p = data.project;
    return {
      name: p.name,
      agentId: p.agentId,
      formConfig: { enabled: false },
      dynamicVariables: {},
      requireMicGate: p.requireMicGate !== false,
      partnerLogo: p.partnerLogo || null,
      primaryColor: p.primaryColor || DEFAULT_BRANDING.primaryColor,
      backgroundColor: p.backgroundColor || DEFAULT_BRANDING.backgroundColor,
      buttonTextColor: p.buttonTextColor || DEFAULT_BRANDING.buttonTextColor,
      landingTitle: resolveLandingText(p.landingTitle, DEFAULT_BRANDING.landingTitle),
      landingSubtitle: resolveLandingText(p.landingSubtitle, DEFAULT_BRANDING.landingSubtitle),
      landingButton: resolveLandingText(p.landingButton, DEFAULT_BRANDING.landingButton),
      landingFooter: resolveLandingText(p.landingFooter, DEFAULT_BRANDING.landingFooter),
    };
  } catch (err) {
    return null;
  }
}

function ElevenLabsWidget() {
  const { projectId } = useParams();
  const [error, setError] = useState(null);
  const [projectConfig, setProjectConfig] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [dynamicVars, setDynamicVars] = useState(null);
  const [ready, setReady] = useState(false);
  const [micError, setMicError] = useState('');
  const [requestingMic, setRequestingMic] = useState(false);
  const wakeLockRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const config = await resolveProject(projectId);
      if (cancelled) return;

      if (!config) {
        setError(`Project "${projectId}" not found`);
        return;
      }

      setProjectConfig(config);

      const search = window.location.search;
      const hasUrlParams = new URLSearchParams(search).toString().length > 0;
      const shouldShowForm = config.formConfig?.enabled && !hasUrlParams;
      setShowForm(shouldShowForm);

      if (!shouldShowForm) {
        const parseResult = parseDynamicVariables(
          window.location.href,
          config.dynamicVariables
        );
        setDynamicVars(parseResult.variables);

        if (hasUrlParams && window.history && window.history.replaceState) {
          window.history.replaceState({}, '', `/${projectId}`);
        }
      }

      if (!shouldShowForm && config.requireMicGate === false) {
        wakeLockRef.current = startWakeLock();
        setReady(true);
      }

      if (!document.querySelector('script[src*="convai-widget-embed"]')) {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
        script.async = true;
        script.type = 'text/javascript';
        document.head.appendChild(script);
      }
    })();

    return () => {
      cancelled = true;
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };
  }, [projectId]);

  const handleStart = async () => {
    setMicError('');
    setRequestingMic(true);
    try {
      await unlockAudio();
      const mic = await requestMicrophoneAccess();
      if (!mic.ok) {
        setMicError(mic.error);
        setRequestingMic(false);
        return;
      }
      wakeLockRef.current = startWakeLock();
      setReady(true);
    } finally {
      setRequestingMic(false);
    }
  };

  if (showForm) {
    return (
      <PreAssistantForm
        projectConfig={projectConfig}
        projectId={projectId}
        onSubmit={() => setShowForm(false)}
      />
    );
  }

  if (error) {
    const safeProjectId = projectId
      ? projectId.replace(/[^a-zA-Z0-9-_]/g, '')
      : 'unknown';
    return (
      <div style={{ ...pageStyle, backgroundColor: '#F7F5F2' }}>
        <p style={{ color: '#DC2626' }}>Project "{safeProjectId}" not found</p>
      </div>
    );
  }

  if (!projectConfig) {
    return (
      <div style={{ ...pageStyle, backgroundColor: '#F7F5F2' }}>
        <p style={{ color: '#666' }}>Loading survey...</p>
      </div>
    );
  }

  const bg = projectConfig.backgroundColor || DEFAULT_BRANDING.backgroundColor;
  const primary = projectConfig.primaryColor || DEFAULT_BRANDING.primaryColor;
  const btnText = projectConfig.buttonTextColor || DEFAULT_BRANDING.buttonTextColor;

  if (!ready) {
    const logoSrc = projectConfig.partnerLogo || '/60-DB.png';
    return (
      <div style={{ ...pageStyle, backgroundColor: bg }}>
        <div style={{ maxWidth: 380, textAlign: 'center', padding: 24 }}>
          <img
            src={logoSrc}
            alt={projectConfig.partnerLogo ? 'Survey logo' : '60 Decibels'}
            style={{
              height: 72,
              width: 'auto',
              maxWidth: '80%',
              marginBottom: 28,
              objectFit: 'contain',
            }}
          />
          {projectConfig.landingTitle ? (
            <h1
              style={{
                fontSize: 24,
                fontWeight: 600,
                margin: '0 0 10px',
                letterSpacing: '-0.02em',
                color: '#1A1A1A',
              }}
            >
              {projectConfig.landingTitle}
            </h1>
          ) : null}
          {projectConfig.landingSubtitle ? (
            <p
              style={{
                fontSize: 15,
                color: 'rgba(26,26,26,0.65)',
                lineHeight: 1.5,
                margin: '0 0 24px',
              }}
            >
              {projectConfig.landingSubtitle}
            </p>
          ) : (
            <div style={{ marginBottom: 24 }} />
          )}
          {micError && (
            <p
              style={{
                color: '#DC2626',
                fontSize: 14,
                marginBottom: 16,
                textAlign: 'left',
                background: 'rgba(220,38,38,0.06)',
                border: '1px solid rgba(220,38,38,0.3)',
                borderRadius: 10,
                padding: 12,
              }}
            >
              {micError}
            </p>
          )}
          <button
            type="button"
            onClick={handleStart}
            disabled={requestingMic}
            aria-label={
              projectConfig.landingButton || DEFAULT_BRANDING.landingButton
            }
            style={{
              width: '100%',
              padding: '16px 20px',
              backgroundColor: requestingMic ? '#E5E5E5' : primary,
              color: requestingMic ? '#999' : btnText,
              border: 'none',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 600,
              cursor: requestingMic ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
              minHeight: 52,
            }}
          >
            {requestingMic
              ? 'Requesting microphone...'
              : projectConfig.landingButton || '\u00a0'}
          </button>
          {projectConfig.landingFooter ? (
            <p
              style={{
                marginTop: 16,
                fontSize: 12,
                color: 'rgba(26,26,26,0.4)',
                lineHeight: 1.4,
              }}
            >
              {projectConfig.landingFooter}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100dvh',
        backgroundColor: '#ffffff',
        overscrollBehavior: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
      }}
    >
      <elevenlabs-convai
        agent-id={projectConfig.agentId}
        dynamic-variables={dynamicVars ? JSON.stringify(dynamicVars) : undefined}
      />
    </div>
  );
}

const pageStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100dvh',
  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  padding: 16,
  boxSizing: 'border-box',
};

export default ElevenLabsWidget;
